// -----------------------------------------------------------------------------
// index.js
// Application entry point for Harbour Nest.
//
// PURPOSE:
// - Mounts the React application to the DOM.
// - Wraps the entire UI in <BrowserRouter> for routing.
// - Wraps the entire UI in <VoiceProvider> to ensure that the
//   voice assistant context is available globally across all pages.
//
// CRITICAL NOTE:
// Any component that uses the useVoiceAssistant() hook MUST be rendered
// somewhere inside <VoiceProvider>. Without this wrapper, the app will throw:
//
//    "useVoiceAssistant must be used within a VoiceProvider"
//
// This file is the correct place to put <VoiceProvider> so the entire app,
// including PortfolioOverview, PropertyDashboard, HomeRunFlow, etc.,
// has full access to voice state and actions.
// -----------------------------------------------------------------------------

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { LoadScript } from "@react-google-maps/api";

import App from "./App.js";

// Auth Context Provider (MUST wrap the entire app for authentication)
import { AuthProvider } from "./context/AuthContext.jsx";

// Voice Context Provider (MUST wrap the entire app)
import { VoiceProvider } from "./components/voice/VoiceContext.jsx";

import "./index.css";

const root = ReactDOM.createRoot(document.getElementById("root"));

// Google Maps libraries to load
const libraries = ["places", "geometry"];

root.render(
  <BrowserRouter>
    <AuthProvider>
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={libraries}
        loadingElement={<div>Loading Maps...</div>}
      >
        <VoiceProvider>
          <App />
        </VoiceProvider>
      </LoadScript>
    </AuthProvider>
  </BrowserRouter>
);
